/* CoachHistorie — tijdlijn van reallocaties per groep, met filter op user.
 * Wordt getoond op de dashboardpagina onder BKR of als standalone widget.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRightLeft, Clock, User } from "lucide-react";
import { toast } from "sonner";

export default function CoachHistorie() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ group_id: "", user_id: "" });
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const gr = await api.get("/groups");
        setGroups(Array.isArray(gr.data) ? gr.data : gr.data?.items || []);
      } catch { /* stil */ }
      try {
        const ur = await api.get("/users");
        setUsers(Array.isArray(ur.data) ? ur.data : ur.data?.items || []);
      } catch { /* stil */ }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.group_id) params.set("group_id", filter.group_id);
      if (filter.user_id) params.set("user_id", filter.user_id);
      params.set("limit", "50");
      const r = await api.get(`/bkr/reallocaties?${params.toString()}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kon historie niet laden");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter.group_id, filter.user_id]);

  return (
    <Card className="p-4 mt-4" data-testid="coach-historie-card">
      <div className="flex items-center gap-2 mb-3">
        <ArrowRightLeft className="h-4 w-4 text-slate-600" />
        <h3 className="font-semibold text-sm">Coach-actie historie</h3>
        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 ml-auto">
          Audit
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        <div>
          <Label className="text-xs">Filter op groep</Label>
          <select value={filter.group_id}
                  onChange={(e) => setFilter({ ...filter, group_id: e.target.value })}
                  data-testid="coach-hist-group-filter"
                  className="w-full border rounded px-2 py-1.5 text-sm bg-white">
            <option value="">Alle groepen</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Filter op gedaan door</Label>
          <select value={filter.user_id}
                  onChange={(e) => setFilter({ ...filter, user_id: e.target.value })}
                  data-testid="coach-hist-user-filter"
                  className="w-full border rounded px-2 py-1.5 text-sm bg-white">
            <option value="">Iedereen</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500">Laden …</p>}

      {!loading && items.length === 0 && (
        <p className="text-sm text-slate-500 italic" data-testid="coach-hist-empty">
          Nog geen reallocaties in deze selectie.
        </p>
      )}

      {!loading && items.length > 0 && (
        <ol className="border-l-2 border-slate-200 pl-4 space-y-3" data-testid="coach-hist-timeline">
          {items.map((it) => (
            <li key={it.id} className="relative" data-testid={`coach-hist-item-${it.id}`}>
              <span className="absolute -left-[9px] top-1.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
              <div className="text-xs text-slate-500 flex items-center gap-2">
                <Clock className="h-3 w-3" />
                {new Date(it.created_at).toLocaleString("nl-NL", {
                  day: "2-digit", month: "short", year: "numeric",
                  hour: "2-digit", minute: "2-digit",
                })}
                {it.by_user_email && (
                  <>
                    <User className="h-3 w-3 ml-2" />
                    <span className="truncate">{it.by_user_email}</span>
                  </>
                )}
              </div>
              <div className="text-sm mt-1">
                <span className="font-medium">{it.from_group_name || "?"}</span>
                <ArrowRightLeft className="inline h-3 w-3 mx-1 text-slate-400" />
                <span className="font-medium text-emerald-700">{it.to_group_name || "?"}</span>
                {it.shift_medewerker_naam && (
                  <span className="text-slate-600"> · {it.shift_medewerker_naam}</span>
                )}
                {(it.shift_start || it.shift_eind) && (
                  <span className="text-slate-500 text-xs"> · {it.shift_start}–{it.shift_eind}</span>
                )}
              </div>
              {it.reason && (
                <p className="text-xs text-slate-600 italic mt-0.5">"{it.reason}"</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
